import React, { Fragment } from "react";
import classNames from "classnames";

import { Popover, Transition } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/20/solid";

import BrightLogoPositive from "../images/logos/bright_logo_positive.svg";
import BrightIsotype from "../images/logos/bright_isotype.svg";

import { company, mainNav, whatsApp } from "../config";

import { LinkButton, Link } from "./";

type Props = {
  // Set to true to hide the navigation elements to encourage the customer to focus
  hideNav?: boolean;

  // Define the image of the partner
  partnerLogo?: string;
};

export const Navigation = ({ hideNav, partnerLogo }: Props) => {
  if (hideNav) {
    return (
      <nav className="w-full sticky top-0 bg-white">
        <div className=" mx-auto max-w-2xl md:max-w-4xl lg:max-w-5xl xl:max-w-7xl flex justify-between items-center px-4 py-6 sm:px-6 lg:justify-start md:space-x-8">
          <Link to="/" className="flex">
            <img className="h-9" src={BrightLogoPositive} alt={company.name} />
          </Link>
        </div>
      </nav>
    );
  }
  if (partnerLogo) {
    return (
      <nav className="w-full sticky top-0 bg-white">
        <div className="mx-auto max-w-2xl md:max-w-4xl lg:max-w-5xl xl:max-w-7xl flex justify-between items-center px-4 py-6 sm:px-6 md:space-x-8">
          <Link to="/" className="flex">
            <img className="h-9" src={BrightLogoPositive} alt="Bright Logo" />
          </Link>
          <Link to="/partner" className="flex">
            <img className="h-9" src={partnerLogo} alt="Partner Logo" />
          </Link>
        </div>
      </nav>
    );
  }

  return (
    <nav className="w-full  sticky top-0 bg-white">
      <Popover className="mx-auto max-w-2xl md:max-w-4xl lg:max-w-5xl xl:max-w-7xl">
        <div className="flex justify-between items-center px-4 py-6 sm:px-6 lg:justify-start md:space-x-8">
          <Link to="/" className="flex">
            <img className="h-9" src={BrightLogoPositive} alt={company.name} />
          </Link>
          <div className="-mr-2 -my-2 lg:hidden">
            <Popover.Button className="bg-white outline-none rounded-full p-2 inline-flex items-center justify-center text-gray-900 hover:text-gray-500 hover:bg-gray-100">
              <Bars3Icon className="h-6 w-6" />
            </Popover.Button>
          </div>
          <div className="hidden lg:flex-1 lg:flex lg:items-center lg:justify-between">
            <Popover.Group as="nav" className="flex space-x-8">
              <Popover className="relative">
                {({ open }) => (
                  <>
                    <Popover.Button
                      className={classNames(
                        open ? "text-gray-900" : "text-gray-500",
                        "group rounded-md inline-flex items-center text-base font-medium hover:text-gray-900",
                      )}
                    >
                      <span>Soluciones</span>
                      <ChevronDownIcon
                        className={classNames(
                          open ? "text-gray-600" : "text-gray-400",
                          "ml-2 h-5 w-5 group-hover:text-gray-500",
                        )}
                      />
                    </Popover.Button>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="opacity-0 translate-y-1"
                      enterTo="opacity-100 translate-y-0"
                      leave="transition ease-in duration-150"
                      leaveFrom="opacity-100 translate-y-0"
                      leaveTo="opacity-0 translate-y-1"
                    >
                      <Popover.Panel className="absolute z-10 -ml-4 mt-3 transform w-screen max-w-md lg:max-w-md">
                        <div className="rounded-md shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden">
                          <div className="relative grid gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8 lg:grid-cols-1">
                            {mainNav.solutions.list.map((item) => (
                              <Link
                                key={item.name}
                                to={item.href}
                                className="-m-3 p-3 flex items-start rounded-md hover:bg-gray-50"
                              >
                                <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-md bg-yellow-500 text-white sm:h-12 sm:w-12">
                                  <item.icon className="h-6 w-6" />
                                </div>
                                <div className="ml-4 flex justify-center items-center h-full">
                                  <p className="text-base font-medium text-gray-900">
                                    {item.name}
                                  </p>
                                  {/* <p className="mt-1 text-sm text-gray-500">
                                    {item.description}
                                  </p> */}
                                </div>
                              </Link>
                            ))}
                          </div>
                        </div>
                      </Popover.Panel>
                    </Transition>
                  </>
                )}
              </Popover>

              {mainNav.primary.map((item, index) => {
                return index < 3 ? (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="text-base font-medium text-gray-500 hover:text-blue-500"
                  >
                    {item.name}
                  </Link>
                ) : null;
              })}
              <Popover className="relative">
                {({ open }) => (
                  <>
                    <Popover.Button
                      className={classNames(
                        open ? "text-gray-900" : "text-gray-500",
                        "outline-none group bg-white rounded-md inline-flex items-center text-base font-medium hover:text-gray-900",
                      )}
                    >
                      <span>Más</span>
                      <ChevronDownIcon
                        className={classNames(
                          open ? "text-gray-600" : "text-gray-400",
                          "ml-2 h-5 w-5 group-hover:text-gray-500",
                        )}
                        aria-hidden="true"
                      />
                    </Popover.Button>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="opacity-0 translate-y-1"
                      enterTo="opacity-100 translate-y-0"
                      leave="transition ease-in duration-150"
                      leaveFrom="opacity-100 translate-y-0"
                      leaveTo="opacity-0 translate-y-1"
                    >
                      <Popover.Panel className="absolute z-10 left-1/2 transform -translate-x-1/2 mt-3 px-2 w-screen max-w-md sm:px-0">
                        <div className="rounded-md shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden">
                          <div className="relative grid gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8">
                            {mainNav.primary.map((item, index) => {
                              return index >= 3 ? (
                                <a
                                  key={item.name}
                                  href={item.href}
                                  className="-m-3 p-3 flex items-start rounded-md hover:bg-gray-50"
                                >
                                  <item.icon
                                    className="flex-shrink-0 h-6 w-6 text-blue-500"
                                    aria-hidden="true"
                                  />
                                  <div className="ml-4">
                                    <p className="text-base font-medium text-gray-900">
                                      {item.name}
                                    </p>
                                    {/* <p className="mt-1 text-sm text-gray-500">
                                      {item.description}
                                    </p> */}
                                  </div>
                                </a>
                              ) : null;
                            })}
                          </div>
                        </div>
                      </Popover.Panel>
                    </Transition>
                  </>
                )}
              </Popover>
            </Popover.Group>
            <div className="flex items-center md:ml-12 space-x-4">
              <LinkButton plain to={mainNav.secondary.signIn.href}>
                {mainNav.secondary.signIn.name}
              </LinkButton>
              <LinkButton plain to={mainNav.secondary.whatsApp.href}>
                <svg className="h-6" fill="currentColor" viewBox="0 0 512 512">
                  <path
                    d="M414.73 97.1A222.14 222.14 0 00256.94 32C134 32 33.92 131.58 33.87 254a220.61 220.61 0 0029.78 111L32 480l118.25-30.87a223.63 223.63 0 00106.6 27h.09c122.93 0 223-99.59 223.06-222A220.18 220.18 0 00414.73 97.1zM256.94 438.66h-.08a185.75 185.75 0 01-94.36-25.72l-6.77-4-70.17 18.32 18.73-68.09-4.41-7A183.46 183.46 0 0171.53 254c0-101.73 83.21-184.5 185.48-184.5a185 185 0 01185.33 184.64c-.04 101.74-83.21 184.52-185.4 184.52zm101.69-138.19c-5.57-2.78-33-16.2-38.08-18.05s-8.83-2.78-12.54 2.78-14.4 18-17.65 21.75-6.5 4.16-12.07 1.38-23.54-8.63-44.83-27.53c-16.57-14.71-27.75-32.87-31-38.42s-.35-8.56 2.44-11.32c2.51-2.49 5.57-6.48 8.36-9.72s3.72-5.56 5.57-9.26.93-6.94-.46-9.71-12.54-30.08-17.18-41.19c-4.53-10.82-9.12-9.35-12.54-9.52-3.25-.16-7-.2-10.69-.2a20.53 20.53 0 00-14.86 6.94c-5.11 5.56-19.51 19-19.51 46.28s20 53.68 22.76 57.38 39.3 59.73 95.21 83.76a323.11 323.11 0 0031.78 11.68c13.35 4.22 25.5 3.63 35.1 2.2 10.71-1.59 33-13.42 37.63-26.38s4.64-24.06 3.25-26.37-5.11-3.71-10.69-6.48z"
                    fillRule="evenodd"
                  />
                </svg>
                <span className="ml-3">WhatsApp</span>
              </LinkButton>
              <LinkButton to={mainNav.secondary.quote.href}>
                {mainNav.secondary.quote.name}
              </LinkButton>
            </div>
          </div>
        </div>

        <Transition
          as={Fragment}
          enter="duration-200 ease-out"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="duration-100 ease-in"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Popover.Panel
            focus
            className="absolute top-0 inset-x-0 p-2 transition transform origin-top-right lg:hidden z-10"
          >
            <div className="rounded-md shadow-lg ring-1 ring-black ring-opacity-5 bg-white divide-y-2 divide-gray-50">
              <div className="pt-5 pb-6 px-5">
                <div className="flex items-center justify-between">
                  <div>
                    <img
                      className="h-9"
                      src={BrightIsotype}
                      alt={company.name}
                    />
                  </div>
                  <div className="-mr-2">
                    <Popover.Button className="bg-white outline-none rounded-full p-2 inline-flex items-center justify-center text-gray-900 hover:text-gray-500 hover:bg-gray-100">
                      <XMarkIcon className="h-6 w-6" />
                    </Popover.Button>
                  </div>
                </div>
                <div className="mt-6">
                  <p className="mt-6 text-left text-base font-medium text-gray-500">
                    Soluciones
                  </p>
                  <nav className="grid mt-3">
                    {mainNav.solutions.list.map((item) => (
                      <a
                        key={item.name}
                        href={item.href}
                        className="p-3 flex items-center rounded-md hover:bg-gray-50"
                      >
                        <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-lg bg-yellow-500 text-white">
                          <item.icon className="h-6 w-6" />
                        </div>
                        <div className="ml-4 text-base font-medium text-gray-900">
                          {item.name}
                        </div>
                      </a>
                    ))}
                  </nav>
                </div>
              </div>
              <div className="py-6 px-5 bg-white">
                <div className="grid grid-cols-2 gap-4">
                  {mainNav.primary.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      className="text-base font-medium text-gray-500 hover:text-blue-500"
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
                <div className="mt-10">
                  <LinkButton size="medium" to={mainNav.secondary.quote.href}>
                    {mainNav.secondary.quote.name}
                  </LinkButton>
                  <LinkButton plain to={mainNav.secondary.whatsApp.href}>
                    <svg
                      className="h-6"
                      fill="currentColor"
                      viewBox="0 0 512 512"
                    >
                      <path
                        d="M414.73 97.1A222.14 222.14 0 00256.94 32C134 32 33.92 131.58 33.87 254a220.61 220.61 0 0029.78 111L32 480l118.25-30.87a223.63 223.63 0 00106.6 27h.09c122.93 0 223-99.59 223.06-222A220.18 220.18 0 00414.73 97.1zM256.94 438.66h-.08a185.75 185.75 0 01-94.36-25.72l-6.77-4-70.17 18.32 18.73-68.09-4.41-7A183.46 183.46 0 0171.53 254c0-101.73 83.21-184.5 185.48-184.5a185 185 0 01185.33 184.64c-.04 101.74-83.21 184.52-185.4 184.52zm101.69-138.19c-5.57-2.78-33-16.2-38.08-18.05s-8.83-2.78-12.54 2.78-14.4 18-17.65 21.75-6.5 4.16-12.07 1.38-23.54-8.63-44.83-27.53c-16.57-14.71-27.75-32.87-31-38.42s-.35-8.56 2.44-11.32c2.51-2.49 5.57-6.48 8.36-9.72s3.72-5.56 5.57-9.26.93-6.94-.46-9.71-12.54-30.08-17.18-41.19c-4.53-10.82-9.12-9.35-12.54-9.52-3.25-.16-7-.2-10.69-.2a20.53 20.53 0 00-14.86 6.94c-5.11 5.56-19.51 19-19.51 46.28s20 53.68 22.76 57.38 39.3 59.73 95.21 83.76a323.11 323.11 0 0031.78 11.68c13.35 4.22 25.5 3.63 35.1 2.2 10.71-1.59 33-13.42 37.63-26.38s4.64-24.06 3.25-26.37-5.11-3.71-10.69-6.48z"
                        fillRule="evenodd"
                      />
                    </svg>
                    <span className="ml-3">WhatsApp</span>
                  </LinkButton>
                  <p className="mt-6 text-center text-base font-medium text-gray-500">
                    ¿Ya eres cliente?
                    <span className="ml-2 ">
                      <Link to={mainNav.secondary.signIn.href}>
                        {mainNav.secondary.signIn.name}
                      </Link>
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </Popover.Panel>
        </Transition>
      </Popover>
    </nav>
  );
};
